import { escapeMarkup } from './sanitizer.js';

/**
 * @typedef {object} RequestGetterOptions
 * @property {boolean} [escaped=true] Indicates whether the value should be escaed
 */

/**
 * Default options object for request getter functions
 * @type {RequestGetterOptions}
 */
const defaultGetterOptions = {
	escaped: true
};

/**
 * Create HTTPS link from HTTP link
 * @param {string} url Full URL address
 * @returns {string}
 */
const forceHttps = (url) => {
	return url.replace('http://', 'https://');
};

/**
 * Create URL instance from the given url address
 * @param {string|URL} url Full URL address or URL instance
 * @returns {URL}
 */
const parseUrl = (url) => {
	return url instanceof URL ? url : new URL(forceHttps(url));
};

/**
 * Get pathname from URL
 * @param {string|URL} url
 * @returns {string}
 */
const getPath = (url) => {
	return parseUrl(url).pathname;
};

/**
 * Get brand name from url
 * @param {string|URL} url Full URL address or URL instance
 * @returns {string}
 */
const getBrandName = (url) => {
	return parseUrl(url).hostname.split('.').reverse()[1] || 'default';
};

/**
 * Get locale from url
 * @param {string|URL} url Full URL address or URL instance
 * @returns {string}
 */
const getLocale = (url) => {
	return getPath(url).split('/')[1];
};

/**
 * Get theme name from url
 * @param {string|URL} url Full URL address or URL instance
 * @returns {string}
 */
const getThemeName = (url) => {
	return getPath(url).split('/')[2] || 'default';
};

/**
 * Get single query parameter from the url by it's name
 * @param {string|URL} url Full URL address or URL instance
 * @param {string} propertyPath Property path to retrieve
 * @param {*} [fallbackValue=null] Fallback value in case of undefined value
 * @param {RequestGetterOptions} [options]
 * @returns {string}
 */
const getQuery = (url, propertyPath, fallbackValue = null, options = defaultGetterOptions) => {
	try {
		const value = parseUrl(url).searchParams.get(propertyPath) ?? fallbackValue;
		return options.escaped ? escapeMarkup(value) : value;
	} catch {
		return fallbackValue;
	}
};

/**
 * Get all query parameters from the url by it's name
 * @param {string|URL} url Full URL address or URL instance
 * @param {string} propertyPath Property path to retrieve
 * @param {*} [fallbackValue=[]] Fallback value in case of undefined value
 * @param {RequestGetterOptions} [options]
 * @returns {string[]}
 */
const getQueryAll = (url, propertyPath, fallbackValue = [], options = defaultGetterOptions) => {
	try {
		const originalValues = parseUrl(url).searchParams.getAll(propertyPath) ?? fallbackValue;
		const values = originalValues.flatMap((value) => value.split(',')).filter((value) => value.length);
		return options.escaped ? values.map(escapeMarkup) : values;
	} catch {
		return fallbackValue;
	}
};

export {
	forceHttps,
	parseUrl,
	getPath,
	getBrandName,
	getLocale,
	getThemeName,
	getQuery,
	getQueryAll
};
